import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import ExpenditurePDFGenerator from './ExpenditurePDFGenerator';

Modal.setAppElement('#root');

const Expenditure = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [expenditures, setExpenditures] = useState([]);
  const [filteredExpenditures, setFilteredExpenditures] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newExpenditure, setNewExpenditure] = useState({ 
    description: '',
    amount: '',
    date: '',
    addedByName: '',
    addedByPhone: ''
  });
  const [editingExpenditure, setEditingExpenditure] = useState(null);
  const [currentExpenditure, setCurrentExpenditure] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFetchingExpenditures, setIsFetchingExpenditures] = useState(true);
  const [isMonthModalOpen, setIsMonthModalOpen] = useState(false);

  const expendituresCollectionRef = collection(db, "expenditures");

  useEffect(() => {
    if (userData) {
      fetchExpenditures();
    }
  }, [userData]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return Number(amount).toLocaleString('en-US');
  };

  useEffect(() => {
    const filtered = expenditures.filter((expenditure) => {
      const formattedDate = expenditure.date ? formatDate(expenditure.date.toDate()) : '';
      const lowerSearchTerm = searchTerm.toLowerCase();
      const description = expenditure.description || '';
      const addedByName = expenditure.addedByName || '';
      return (
        description.toLowerCase().includes(lowerSearchTerm) ||
        formattedDate.toLowerCase().includes(lowerSearchTerm) ||
        addedByName.toLowerCase().includes(lowerSearchTerm)
      );
    });
    setFilteredExpenditures(filtered);
  }, [searchTerm, expenditures]);

  const fetchExpenditures = async () => {
    setIsFetchingExpenditures(true);
    try {
      const data = await getDocs(expendituresCollectionRef);
      let expenditureList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      // Filter by branch if userData.branch is available and not 'N/A'
      if (userData?.branch && userData.branch !== "N/A") {
        expenditureList = expenditureList.filter((expenditure) => expenditure.branch === userData.branch);
      }
      // Sort expenditures by date in descending order (latest first)
      expenditureList.sort((a, b) => b.date.toDate().getTime() - a.date.toDate().getTime());
      setExpenditures(expenditureList);
      setFilteredExpenditures(expenditureList);
    } catch (error) {
      setError("Failed to fetch expenditures");
      setShowModal(true);
    } finally {
      setIsFetchingExpenditures(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewExpenditure({ ...newExpenditure, [name]: value });
  };

  const handleAddExpenditure = async () => {
    if (!newExpenditure.description.trim() || !newExpenditure.amount) {
      setError("Description and amount cannot be empty");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(expendituresCollectionRef, {
        ...newExpenditure,
        amount: parseInt(newExpenditure.amount),
        date: newExpenditure.date ? Timestamp.fromDate(new Date(newExpenditure.date)) : Timestamp.now(),
        addedByName: `${userData.firstName} ${userData.lastName}` || 'Unknown',
        addedByPhone: userData.phone || '',
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Expenditure added successfully");
      fetchExpenditures();
      setNewExpenditure({ 
        description: '',
        amount: '',
        date: '',
        addedByName: '',
        addedByPhone: ''
      });
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add expenditure");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateExpenditure = async () => {
    setUpdate(true);
    try {
      const expenditureDoc = doc(db, "expenditures", editingExpenditure.id);
      await updateDoc(expenditureDoc, {
        ...newExpenditure,
        amount: parseInt(newExpenditure.amount),
        date: newExpenditure.date ? Timestamp.fromDate(new Date(newExpenditure.date)) : Timestamp.now(),
        addedByName: `${userData.firstName} ${userData.lastName}` || 'Unknown',
        addedByPhone: userData.phone || ''
      });
      setSuccessMessage("Expenditure updated successfully");
      fetchExpenditures();
      setEditingExpenditure(null);
      setNewExpenditure({ 
        description: '',
        amount: '',
        date: '',
        addedByName: '',
        addedByPhone: ''
      });
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update expenditure");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEditExpenditure = (expenditureToEdit) => {
    setEditingExpenditure(expenditureToEdit);
    setNewExpenditure({ 
      description: expenditureToEdit.description,
      amount: expenditureToEdit.amount.toString(),
      date: expenditureToEdit.date.toDate().toISOString().split('T')[0],
      addedByName: expenditureToEdit.addedByName,
      addedByPhone: expenditureToEdit.addedByPhone
    });
    setIsEditModalOpen(true);
  };

  const confirmDeleteExpenditure = (expenditureToDelete) => {
    setCurrentExpenditure(expenditureToDelete);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteExpenditure = async (id) => {
    setUpdate(true);
    try {
      const expenditureDoc = doc(db, "expenditures", id);
      await deleteDoc(expenditureDoc);
      setSuccessMessage("Expenditure deleted successfully");
      fetchExpenditures();
    } catch (error) {
      setError("Failed to delete expenditure");
    } finally {
      setUpdate(false);
      setIsDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  const getMonthsSoFar = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-based (0 = January, 11 = December)
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months.slice(0, currentMonth + 1).map((month, index) => ({
      name: month,
      value: `${currentYear}-${(index + 1).toString().padStart(2, "0")}`, // e.g., "2025-09"
    }));
  };

  const months = getMonthsSoFar();

  if (loading) return <Loader text={"LOADING"} />;
  if (!userData) return <Loader text={"READING"} />;

  const allowedRoles = ['Admin', 'Supreme', 'Cashier'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Manage Expenditures</h2>
            <Link to="#" className="home-link" onClick={() => fetchExpenditures()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed4">            
            <div className="row justify-center align-center gap-15 add-buttons">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Expenditure</button>
              <ExpenditurePDFGenerator expenditures={filteredExpenditures} days={7} buttonText="7 Days" />
              <button className="login-button login-button-d caps" onClick={() => setIsMonthModalOpen(true)}>Monthly</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by description, date (e.g., 14 Sep 2025), or person"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {isFetchingExpenditures ? (
                  <p className="loading-text">Loading expenditures ...</p>
                ) : filteredExpenditures.length === 0 ? (
                  <p className="loading-text">No expenditures match your search.</p>
                ) : (
                  filteredExpenditures.map((expenditureItem) => (
                    <div key={expenditureItem.id} className="class-item">
                      <div className="column gap-1">
                        <p>
                          <strong>{expenditureItem.description}</strong><br />
                          <span>₦{formatAmount(expenditureItem.amount)}</span><br />
                          <span className="size5">by {expenditureItem.addedByName} ({expenditureItem.addedByPhone || 'N/A'})                          
                          </span><br />
                          {expenditureItem.branch && expenditureItem.branch !== 'N/A' && (
                            <span className="size5">{expenditureItem.branch}<br /></span>
                          )}
                          <span className="size5 font-gold bold">{formatDate(expenditureItem.date.toDate())}</span><br />                          
                        </p>                        
                      </div>
                      <div className="row align-center justify-center gap-15">
                        <button className="list-btn" onClick={() => handleEditExpenditure(expenditureItem)}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="list-btn delete-btn" onClick={() => confirmDeleteExpenditure(expenditureItem)}>
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat Kursiv align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Expenditure"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Expenditure</h2>
        <form className="session-form">
          <input
            type="text"
            name="description"
            className="input-field"
            placeholder="Description"
            value={newExpenditure.description}
            onChange={handleInputChange}
            required
          />
          <input
            type="number"
            name="amount"
            className="input-field"
            placeholder="Amount"
            value={newExpenditure.amount}
            onChange={handleInputChange}
            required
            min="0"
            step="1"
          />
          <input
            type="date"
            name="date"
            className="input-field"
            value={newExpenditure.date}
            onChange={handleInputChange}
          />
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddExpenditure}>Add</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setNewExpenditure({ 
            description: '',
            amount: '',
            date: '',
            addedByName: '',
            addedByPhone: ''
          });
          setEditingExpenditure(null);
        }}
        contentLabel="Edit Expenditure"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Expenditure</h2>
        <form className="session-form">
          <input
            type="text"
            name="description"
            className="input-field"
            placeholder="Description"
            value={newExpenditure.description}
            onChange={handleInputChange}
            required
          />
          <input
            type="number"
            name="amount"
            className="input-field"
            placeholder="Amount"
            value={newExpenditure.amount}
            onChange={handleInputChange}
            required
            min="0"
            step="1"
          />
          <input
            type="date"
            name="date"
            className="input-field"
            value={newExpenditure.date}
            onChange={handleInputChange}
          />
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
                setIsEditModalOpen(false);
                setNewExpenditure({ 
                  description: '',
                  amount: '',
                  date: '',
                  addedByName: '',
                  addedByPhone: ''
                });
                setEditingExpenditure(null);
              }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateExpenditure}>Update</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Expenditure"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Expenditure</h2>
        {currentExpenditure && (
          <div>
            <p>Are you sure you want to delete <strong>{currentExpenditure.description}</strong>?</p>
            <p>This expenditure will be permanently removed.</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" type="button" onClick={() => handleDeleteExpenditure(currentExpenditure.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isMonthModalOpen}
        onRequestClose={() => setIsMonthModalOpen(false)}
        contentLabel="Select Month"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <p className="size5 bold caps">Select a Month</p>
        <div className="column modal-scroll">
          {months.map((month, index) => (
            <ExpenditurePDFGenerator
              key={index}
              expenditures={filteredExpenditures}
              month={month.value}
              buttonText={`${index + 1}. ${month.name}`}
            />
          ))}
        </div>
        <button type="button" className="login-button caps" onClick={() => setIsMonthModalOpen(false)}>
          Close
        </button>
      </Modal>
    </>
  );
};

export default Expenditure;